import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, Zap, TrendingUp, Award, Target, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import { WinProtectOptimizer, WinProtectOptimizationResult, WinProtectTestResult } from '@/services/win-protect-optimizer';
import { ParsedCsvRow, AnalysisParams } from './types';
import { formatCurrency } from '@/utils/formatters';

interface AutoWinProtectOptimizerProps {
  csvData: ParsedCsvRow[];
  params: AnalysisParams;
  onApplySettings: (startWins: number, maxWins: number) => void;
}

export const AutoWinProtectOptimizer: React.FC<AutoWinProtectOptimizerProps> = ({
  csvData,
  params,
  onApplySettings,
}) => {
  const [maxWinsToTest, setMaxWinsToTest] = useState(10);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [results, setResults] = useState<WinProtectTestResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [optimizationMode, setOptimizationMode] = useState<'single' | 'compare'>('single');
  const [selectedMode, setSelectedMode] = useState<string>('consecutive_trades');

  const handleOptimize = async () => {
    if (csvData.length === 0) {
      return;
    }

    setIsOptimizing(true);
    try {
      // Run optimization in a setTimeout to avoid blocking UI
      setTimeout(() => {
        let optimizationResults: WinProtectTestResult[];
        
        if (optimizationMode === 'compare') {
          optimizationResults = WinProtectOptimizer.compareAllModes(csvData, maxWinsToTest, params);
        } else {
          optimizationResults = WinProtectOptimizer.findTopWinProtectSettings(
            csvData,
            maxWinsToTest,
            params,
            selectedMode
          );
        }
        
        setResults(optimizationResults);
        setIsOpen(true);
        setIsOptimizing(false);
      }, 100);
    } catch (error) {
      console.error('Win protect optimization failed:', error);
      setIsOptimizing(false);
    }
  };

  const handleApplySettings = (startWins: number, maxWins: number) => {
    onApplySettings(startWins, maxWins);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'optimal':
        return <Badge className="bg-green-500 hover:bg-green-600">✅ Optimal</Badge>;
      case 'good':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">⚠️ Good</Badge>;
      default:
        return <Badge variant="destructive">❌ Poor</Badge>;
    }
  };

  const getRatioBadge = (ratio: number) => {
    if (ratio >= 3.0) {
      return <span className="text-green-600 font-semibold">{ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>;
    } else if (ratio >= 2.0) {
      return <span className="text-yellow-600 font-semibold">{ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>;
    } else {
      return <span className="text-red-600 font-semibold">{ratio === Infinity ? '∞' : ratio.toFixed(2)}</span>;
    }
  };

  const getWinRateBadge = (winRate: number) => {
    if (winRate <= 50) {
      return <span className="text-red-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else if (winRate <= 70) {
      return <span className="text-orange-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else if (winRate <= 75) {
      return <span className="text-yellow-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else {
      return <span className="text-green-600 font-semibold">{winRate.toFixed(1)}%</span>;
    }
  };

  const getModeBadge = (mode: string) => {
    const isTradesMode = mode === 'consecutive_trades';
    return (
      <Badge variant={isTradesMode ? "default" : "secondary"}>
        {isTradesMode ? "Trades" : "Losses"}
      </Badge>
    );
  };

  const getBestResultBadge = (index: number) => {
    if (index === 0 && optimizationMode === 'compare') {
      return <Crown className="h-4 w-4 text-yellow-500" />;
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Zap size={18} />
          Auto Win Protect Optimizer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-3">
            <Label>Optimization Mode</Label>
            <RadioGroup 
              value={optimizationMode} 
              onValueChange={(value: 'single' | 'compare') => setOptimizationMode(value)}
              className="grid grid-cols-2 gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single" />
                <Label htmlFor="single" className="text-sm">Single Mode</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="compare" id="compare" />
                <Label htmlFor="compare" className="text-sm">Compare Both Modes</Label>
              </div>
            </RadioGroup>
          </div>
          
          {optimizationMode === 'single' && (
            <div className="space-y-3">
              <Label>Stop Mode</Label>
              <RadioGroup 
                value={selectedMode} 
                onValueChange={setSelectedMode}
                className="grid grid-cols-1 gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_trades" id="trades_mode" />
                  <Label htmlFor="trades_mode" className="text-sm">Stop after M trades (any outcome)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_losses" id="losses_mode" />
                  <Label htmlFor="losses_mode" className="text-sm">Stop after M consecutive losses</Label>
                </div>
              </RadioGroup>
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Maximum wins to test</Label>
            <Input
              type="number"
              min="3"
              max="20"
              value={maxWinsToTest}
              onChange={(e) => setMaxWinsToTest(parseInt(e.target.value) || 10)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              The optimizer will test combinations of start wins (2-10) and max wins (1-{maxWinsToTest})
              {optimizationMode === 'compare' && ' across both modes'}
            </p>
          </div>
        </div>

        <Button 
          onClick={handleOptimize}
          disabled={isOptimizing || csvData.length === 0}
          className="w-full"
        >
          {isOptimizing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Optimizing...
            </>
          ) : (
            <>
              <TrendingUp className="mr-2 h-4 w-4" />
              {optimizationMode === 'compare' ? 'Compare All Modes' : 'Find Optimal Settings'}
            </>
          )}
        </Button>

        {results.length > 0 && (
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors p-2 rounded border">
                <span>
                  Found {results.length} settings ({results.filter(r => r.status === 'optimal').length} optimal)
                  {optimizationMode === 'compare' && ' across both modes'}
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <div className="mt-4 rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Start Wins</TableHead>
                      <TableHead className="w-[80px]">Max Wins</TableHead>
                      {optimizationMode === 'compare' && <TableHead className="w-[80px]">Mode</TableHead>}
                      <TableHead className="text-right">Total Profit</TableHead>
                      <TableHead className="text-right">Protected Profit</TableHead>
                      <TableHead className="text-right">Max DD</TableHead>
                      <TableHead className="text-right">Profit/DD</TableHead>
                      <TableHead className="text-right">Win Rate</TableHead>
                      <TableHead className="text-right">Effectiveness</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[80px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow key={`${result.startWins}-${result.maxWins}-${result.mode || 'default'}`}>
                        <TableCell className="text-center font-semibold flex items-center gap-2">
                          {getBestResultBadge(index)}
                          {result.startWins}
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {result.maxWins}
                        </TableCell>
                        {optimizationMode === 'compare' && (
                          <TableCell>{getModeBadge(result.mode || 'consecutive_trades')}</TableCell>
                        )}
                        <TableCell className="text-right font-semibold">
                          <span className={result.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(result.totalProfit)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-green-600 font-medium">
                            {formatCurrency(result.protectedProfit)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(result.maxDrawdown)}
                        </TableCell>
                        <TableCell className="text-right">
                          {getRatioBadge(result.profitDDRatio)}
                        </TableCell>
                        <TableCell className="text-right">
                          {getWinRateBadge(result.winRate)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-orange-600">
                            {result.effectiveness.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(result.status)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApplySettings(result.startWins, result.maxWins)}
                          >
                            Apply
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-3 rounded border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2">
            <Target size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="text-blue-800 dark:text-blue-300">Anti Look-ahead Bias:</strong>
              <br />
              • Always skips the Nth winning trade to avoid look-ahead bias
              • Uses time-series validation with forward-walking analysis
              • Tests combinations systematically to find optimal profit protection
              {optimizationMode === 'compare' && (
                <>
                  <br />
                  • <strong>Compare mode:</strong> Tests both "trades" and "losses" modes to find the best approach
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};